import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { Search, ArrowRight, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { PageHeader } from '@/components/common';
import { NAV_GROUPS, MODULE_BY_PATH, type ModuleEntry } from '@/lib/modules';
import { useAuth } from '@/context/AuthContext';

/**
 * The whole operation on one page.
 *
 * The rail tells you what exists and the popover tells you what one screen is
 * for. Neither answers the question a new person actually has, which is how the
 * pieces relate — that receipts feed putaway, that putaway feeds the yard, that
 * consumption feeds forecasting which feeds purchase orders. That shape is the
 * thing an operations platform is, and it was previously only learnable by
 * clicking around for a month.
 *
 * Groups are laid out as numbered stages in the order work actually moves, so
 * the page reads as a sequence rather than as a menu. Search filters across
 * titles, purposes and the questions each screen answers, because somebody
 * looking for "where do I see what a job cost" will not type "consumption".
 */
export default function Guide() {
  const { hasPermission } = useAuth();

  /**
   * The guide describes the whole product, and until now it described it to
   * everybody. That was fine while every login was an employee; with the
   * customer portal it meant an external company could read the map of every
   * internal screen — the yard, the freight sourcing, the purchase orders —
   * along with what each one is for.
   *
   * No data leaked, but the shape of the business did. Same rule as the rail:
   * a group the reader cannot open is a group they are not shown.
   */
  const readable = NAV_GROUPS.filter(
    (g) =>
      (!g.permission || hasPermission(g.permission)) &&
      (!g.excludePermission || !hasPermission(g.excludePermission))
  );

  const [query, setQuery] = useState('');

  const q = query.trim().toLowerCase();

  /**
   * Words that carry no signal in a query like "what did it cost". Searching on
   * the whole phrase found nothing, which made the placeholder's own example
   * fail — so the query is reduced to the words that mean something and an
   * entry matches if it contains any of them.
   */
  const STOPWORDS = new Set([
    'a',
    'an',
    'and',
    'are',
    'as',
    'at',
    'be',
    'by',
    'did',
    'do',
    'does',
    'for',
    'from',
    'has',
    'have',
    'how',
    'i',
    'in',
    'is',
    'it',
    'me',
    'my',
    'of',
    'on',
    'or',
    'our',
    'see',
    'show',
    'that',
    'the',
    'this',
    'to',
    'was',
    'we',
    'what',
    'when',
    'where',
    'which',
    'who',
    'why',
    'will',
    'with',
  ]);

  const tokens = q.split(/[^a-z0-9]+/).filter((w) => w.length > 1 && !STOPWORDS.has(w));

  const haystack = (m: ModuleEntry) =>
    [m.title, m.purpose, m.question, ...(m.terms ?? []).flatMap((t) => [t.term, t.means])]
      .join(' ')
      .toLowerCase();

  const score = (m: ModuleEntry) => {
    if (tokens.length === 0) return 1;
    const hay = haystack(m);
    return tokens.filter((t) => hay.includes(t)).length;
  };

  /**
   * Strict first, loose only if strict finds nothing.
   *
   * Requiring every word keeps a technical phrase precise — "heat number"
   * should return the two screens that talk about heat numbers, not the nine
   * that contain the word "number". Falling back to any-word keeps a
   * conversational query useful — "what did it cost" has no screen containing
   * both remaining words, and returning nothing there would be unhelpful.
   */
  const strictHits = readable.flatMap((g) => g.children).filter(
    (m) => tokens.length > 0 && score(m) === tokens.length
  ).length;
  const threshold = strictHits > 0 ? tokens.length : 1;

  const matches = (m: ModuleEntry) => !q || score(m) >= threshold;

  // No memo: fifty-nine entries filtered on a keystroke is not work worth
  // caching, and keying the cache on `q` alone while the predicates close over
  // derived state is the shape stale-closure bugs come from.
  const groups = readable.map((g) => ({
    ...g,
    // Hidden sub-screens are searchable here even though they never appear in
    // the rail — landing on one from a link is exactly when somebody needs to
    // know what it is. Best match first, so a query hitting several words
    // surfaces the screen carrying all of them.
    visible: g.children.filter(matches).sort((a, b) => score(b) - score(a)),
  })).filter((g) => g.visible.length > 0);

  const total = readable.reduce((n, g) => n + g.children.length, 0);
  const shown = groups.reduce((n, g) => n + g.visible.length, 0);

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          breadcrumbs={[{ label: 'Home', href: '/' }, { label: 'Guide' }]}
          title="Platform Guide"
          description="Every screen, what it records, and what flows into and out of it."
        />
      </motion.div>

      {/* The operation in one line. Ten group names in work order is the single
          most useful thing on the page, and it fits above the fold.

          Hidden below two groups: a customer-portal login can read one screen,
          and a lone chip captioned "in order" with no arrow after it promises a
          sequence and delivers a label. */}
      {readable.length > 1 && (
      <motion.div variants={fadeUp}>
        <Card>
          <CardContent className="p-4">
            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              The operation, in order
            </p>
            <div className="flex flex-wrap items-center gap-x-1.5 gap-y-2">
              {readable.map((g, i) => (
                <span key={g.code} className="flex items-center gap-1.5">
                  <a
                    href={`#${g.code}`}
                    className="rounded border px-2 py-1 text-sm transition-colors hover:border-primary hover:text-primary"
                  >
                    {g.title}
                  </a>
                  {i < readable.length - 1 && (
                    <ArrowRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground/50" />
                  )}
                </span>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
      )}

      <motion.div variants={fadeUp}>
        <div className="relative max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search screens, purposes and terms. Try “heat number” or “what did it cost”"
            className="pl-9"
            aria-label="Search the guide"
          />
        </div>
        {q && (
          <p className="mt-2 text-sm text-muted-foreground">
            {shown} of {total} screens match “{query}”
          </p>
        )}
      </motion.div>

      {groups.map((group, gi) => (
        <motion.section key={group.code} id={group.code} variants={fadeUp} className="scroll-mt-4">
          <div className="mb-3 flex items-baseline gap-3 border-b pb-2">
            <span className="font-mono text-xs font-semibold tabular-nums text-muted-foreground">
              {String(gi + 1).padStart(2, '0')}
            </span>
            <h2 className="text-lg font-semibold tracking-tight text-foreground">{group.title}</h2>
            <p className="min-w-0 flex-1 text-sm text-muted-foreground">{group.blurb}</p>
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            {group.visible.map((m) => {
              const feeds = (m.upstream ?? []).map((p) => MODULE_BY_PATH[p]).filter(Boolean);
              const serves = (m.downstream ?? []).map((p) => MODULE_BY_PATH[p]).filter(Boolean);
              return (
                <Card key={m.path} className="transition-colors hover:border-primary/40">
                  <CardContent className="p-4">
                    <div className="flex items-baseline gap-2">
                      <Link
                        to={m.path}
                        className="font-medium text-foreground underline-offset-4 hover:underline"
                      >
                        {m.title}
                      </Link>
                      {m.hidden && (
                        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                          sub-screen
                        </span>
                      )}
                    </div>

                    <p className="mt-1 text-sm text-muted-foreground">{m.purpose}</p>
                    <p className="mt-2 text-sm italic text-foreground/80">“{m.question}”</p>

                    {(feeds.length > 0 || serves.length > 0) && (
                      <div className="mt-3 space-y-1.5 border-t pt-3">
                        {feeds.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ArrowDownLeft className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                              from
                            </span>
                            {feeds.map((f) => (
                              <Link
                                key={f.path}
                                to={f.path}
                                className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-foreground"
                              >
                                {f.title}
                              </Link>
                            ))}
                          </div>
                        )}
                        {serves.length > 0 && (
                          <div className="flex flex-wrap items-center gap-1.5">
                            <ArrowUpRight className="h-3 w-3 shrink-0 text-muted-foreground" />
                            <span className="text-[11px] uppercase tracking-wider text-muted-foreground">
                              to
                            </span>
                            {serves.map((f) => (
                              <Link
                                key={f.path}
                                to={f.path}
                                className="rounded border px-1.5 py-0.5 text-xs text-muted-foreground hover:border-primary hover:text-foreground"
                              >
                                {f.title}
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {m.terms && m.terms.length > 0 && (
                      <dl className="mt-3 space-y-1 border-t pt-3">
                        {m.terms.map((t) => (
                          <div key={t.term} className="text-xs">
                            <dt className="inline font-semibold text-foreground">{t.term} — </dt>
                            <dd className="inline text-muted-foreground">{t.means}</dd>
                          </div>
                        ))}
                      </dl>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </motion.section>
      ))}

      {groups.length === 0 && (
        <motion.p variants={fadeUp} className="py-12 text-center text-muted-foreground">
          Nothing matches “{query}”. Try a term from one of the screens above.
        </motion.p>
      )}
    </motion.main>
  );
}
